/**
 * Quota dashboard — tenant view (read-only).
 *
 * Shows current usage vs limits across the tenant's custom-module
 * surface (entities, workflows, webhooks, records, and recent
 * workflow run rate). Each metric renders as a usage bar so the
 * admin can spot crowding before hitting a wall.
 *
 * Limits are set by platform staff, not by the tenant — raising your
 * own ceiling would defeat the purpose. Tenant admins who need more
 * headroom should contact platform support. The edit surface lives at
 * `/api/platform/admin/tenants/:id/quotas` on the platform admin plane.
 */

import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/quotas')({
  component: QuotasPage,
})

function QuotasPage() {
  const { data, isLoading } = useQuery(orpc.modules.custom.quotas.get.queryOptions())

  if (isLoading || !data) {
    return (
      <div className="container mx-auto py-6 px-4 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const { quotas, usage } = data

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Quotas</h1>
        <p className="text-sm text-muted-foreground">
          Per-tenant limits enforced by the API. Contact platform support if you need more headroom.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <UsageBar
            label="Entities (across all modules)"
            current={usage.entities}
            max={quotas.maxEntitiesPerModule}
          />
          <UsageBar
            label="Workflows"
            current={usage.workflows}
            max={quotas.maxWorkflowsPerModule}
          />
          <UsageBar label="Webhooks" current={usage.webhooks} max={quotas.maxWebhooksPerModule} />
          <UsageBar
            label="Live records"
            current={usage.records}
            max={Number.POSITIVE_INFINITY}
            note={`${usage.records.toLocaleString()} active records (no per-tenant cap; only per-record size cap is enforced)`}
          />
          <UsageBar
            label="Workflow runs (last minute)"
            current={usage.workflowRunsLastMinute}
            max={quotas.maxWorkflowRunsPerMinute}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current limits</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <LimitRow label="Max entities per module" value={quotas.maxEntitiesPerModule} />
            <LimitRow label="Max fields per entity" value={quotas.maxFieldsPerEntity} />
            <LimitRow
              label="Max record size"
              value={`${(quotas.maxRecordSizeBytes / 1024).toLocaleString()} KB`}
            />
            <LimitRow label="Max workflows per module" value={quotas.maxWorkflowsPerModule} />
            <LimitRow label="Max webhooks per module" value={quotas.maxWebhooksPerModule} />
            <LimitRow label="Max workflow runs / minute" value={quotas.maxWorkflowRunsPerMinute} />
          </dl>
        </CardContent>
      </Card>
    </div>
  )
}

function LimitRow({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex justify-between items-baseline border-b pb-2 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums font-medium">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </dd>
    </div>
  )
}

function UsageBar({
  label,
  current,
  max,
  note,
}: {
  label: string
  current: number
  max: number
  note?: string
}) {
  const pct = Number.isFinite(max) && max > 0 ? Math.min(100, (current / max) * 100) : 0
  const danger = pct >= 90
  const warn = !danger && pct >= 70
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span>{label}</span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {note ??
            `${current.toLocaleString()} / ${Number.isFinite(max) ? max.toLocaleString() : '∞'}`}
        </span>
      </div>
      {Number.isFinite(max) && (
        <div className="mt-1 h-2 w-full bg-muted rounded">
          <div
            className={`h-full rounded transition-all ${
              danger ? 'bg-destructive' : warn ? 'bg-amber-500' : 'bg-primary'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  )
}
